import React, { useEffect, useState } from 'react';
import { Card, Form, Switch, InputNumber, Input, Button, Space, Typography, message, Divider, Spin } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetIntegritySettingsQuery,
    useUpdateIntegritySettingsMutation,
} from '../../../services/nestjs-base-api';
import { LlmCostPanel } from '../../components/response-integrity/LlmCostPanel';

const { Title, Text } = Typography;

interface SettingsState {
    enabled: boolean;
    cleanMin: number;
    suspectMin: number;
    flaggedMin: number;
    llmBudgetPerSurveyUsd: number;
    llmAlertRecipients: string;
    evidenceRetentionDays: number;
}

export const IntegritySettingsPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const { data, isLoading, refetch } = useGetIntegritySettingsQuery({
        jwtToken: user?.jwtToken || '',
    });
    const [updateSettings, { isLoading: saving }] =
        useUpdateIntegritySettingsMutation();

    const [state, setState] = useState<SettingsState | null>(null);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (data) {
            setState({
                enabled: !!data.enabled,
                cleanMin: Number(data.cleanMin ?? 80),
                suspectMin: Number(data.suspectMin ?? 60),
                flaggedMin: Number(data.flaggedMin ?? 30),
                llmBudgetPerSurveyUsd: Number(data.llmBudgetPerSurveyUsd ?? 50),
                llmAlertRecipients: String(data.llmAlertRecipients ?? 'org_admins'),
                evidenceRetentionDays: Number(data.evidenceRetentionDays ?? 365),
            });
            setDirty(false);
        }
    }, [data]);

    const update = <K extends keyof SettingsState>(
        key: K,
        value: SettingsState[K],
    ) => {
        if (!state) return;
        setState({ ...state, [key]: value });
        setDirty(true);
    };

    const handleSave = async () => {
        if (!state) return;
        try {
            await updateSettings({
                jwtToken: user?.jwtToken || '',
                enabled: state.enabled,
                cleanMin: state.cleanMin,
                suspectMin: state.suspectMin,
                flaggedMin: state.flaggedMin,
                llmBudgetPerSurveyUsd: state.llmBudgetPerSurveyUsd,
                llmAlertRecipients: state.llmAlertRecipients,
                evidenceRetentionDays: state.evidenceRetentionDays,
            }).unwrap();
            message.success(intl.formatMessage({ id: 'responseIntegrity.settings.saved' }));
            setDirty(false);
            refetch();
        } catch (e) {
            message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="responseIntegrity.settings.title" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage id="responseIntegrity.settings.subtitle" />
                    </Text>
                </div>

                <Card>
                    {isLoading || !state ? (
                        <Spin />
                    ) : (
                        <Form layout="vertical">
                            <Form.Item label={<FormattedMessage id="responseIntegrity.settings.enabled" />}>
                                <Switch
                                    checked={state.enabled}
                                    onChange={(v) => update('enabled', v)}
                                />
                            </Form.Item>
                            <Divider orientation="left">
                                <FormattedMessage id="responseIntegrity.settings.tierThresholds" />
                            </Divider>
                            <Space wrap>
                                <Form.Item label={<FormattedMessage id="responseIntegrity.settings.cleanMin" />}>
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        value={state.cleanMin}
                                        onChange={(v) =>
                                            typeof v === 'number' && update('cleanMin', v)
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label={<FormattedMessage id="responseIntegrity.settings.suspectMin" />}>
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        value={state.suspectMin}
                                        onChange={(v) =>
                                            typeof v === 'number' && update('suspectMin', v)
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label={<FormattedMessage id="responseIntegrity.settings.flaggedMin" />}>
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        value={state.flaggedMin}
                                        onChange={(v) =>
                                            typeof v === 'number' && update('flaggedMin', v)
                                        }
                                    />
                                </Form.Item>
                            </Space>
                            <Divider orientation="left">LLM judge</Divider>
                            <Space wrap>
                                <Form.Item label={<FormattedMessage id="responseIntegrity.settings.llmBudget" />}>
                                    <InputNumber
                                        min={0}
                                        step={1}
                                        value={state.llmBudgetPerSurveyUsd}
                                        onChange={(v) =>
                                            typeof v === 'number' &&
                                            update('llmBudgetPerSurveyUsd', v)
                                        }
                                    />
                                </Form.Item>
                                <Form.Item label={<FormattedMessage id="responseIntegrity.settings.llmAlertRecipients" />}>
                                    <Input
                                        value={state.llmAlertRecipients}
                                        onChange={(e) =>
                                            update('llmAlertRecipients', e.target.value)
                                        }
                                        style={{ width: 220 }}
                                    />
                                </Form.Item>
                            </Space>
                            <Divider orientation="left">Retention</Divider>
                            <Form.Item label={<FormattedMessage id="responseIntegrity.settings.evidenceRetention" />}>
                                <InputNumber
                                    min={1}
                                    step={1}
                                    value={state.evidenceRetentionDays}
                                    onChange={(v) =>
                                        typeof v === 'number' && update('evidenceRetentionDays', v)
                                    }
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={!dirty}
                                >
                                    Save settings
                                </Button>
                            </Form.Item>
                        </Form>
                    )}
                </Card>

                <LlmCostPanel />
            </Space>
        </div>
    );
};
